import React, { useMemo, useState } from 'react';
import RMTable from 'components/customComponents/Controls/RMTable';
import { DateFilters, PlateInspectionsToScoreCols, getHeaderDeleteButton } from './utils';
import {
  ImagingSchedulesDeletedDataDTO,
  PlateInspectionsToScoreDTO,
} from 'business/models/Models';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { setImagingSchedulesIsDirty } from 'redux/reducers/imagingSchedulesReducer';
import { useDispatch } from 'react-redux';
interface InspectionsToScoreProps {
  plateInspectionsToScore: PlateInspectionsToScoreDTO[];
  setPlateInspectionsToScore: React.Dispatch<
    React.SetStateAction<PlateInspectionsToScoreDTO[]>
  >;
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>;
}

function InspectionsToScore(props: InspectionsToScoreProps) {
  const { plateInspectionsToScore, setPlateInspectionsToScore, setDeletedData } = props;
  const [selectedIds, setSelectedIds] = useState<number[]>();
  const [selectedOption, setSelectedOption] = useState<string>('all');
  const dispatch = useDispatch();

  const tableData = useMemo(() => {
    if (plateInspectionsToScore) {
      if (selectedOption === 'all') {
        return plateInspectionsToScore?.map((p: PlateInspectionsToScoreDTO) => ({
          id: p.id,
          dateImaged: p.dateImaged,
          user: p.user,
          experimentName: p.experimentName,
          experimentId: p.experimentId,
          plateNumber: p.plateNumber,
        }));
      } else {
        return plateInspectionsToScore
          ?.filter((item) => {
            const days = parseInt(selectedOption, 10);
            const currentDate = new Date();
            const itemDate = new Date(item.dateImaged);
            const timeDiff = currentDate.getTime() - itemDate.getTime();
            const diffDays = timeDiff / (1000 * 3600 * 24);
            return diffDays <= days;
          })
          .map((p: PlateInspectionsToScoreDTO) => ({
            id: p.id,
            dateImaged: p.dateImaged,
            user: p.user,
            experimentName: p.experimentName,
            experimentId: p.experimentId,
            plateNumber: p.plateNumber,
          }));
      }
    }
    return [];
  }, [plateInspectionsToScore, selectedOption, selectedIds]);

  const onDelete = () => {
    const ids = selectedIds.map((i) => plateInspectionsToScore[i]?.id);
    const newData = plateInspectionsToScore.filter((x) => !ids.includes(x.id));
    setPlateInspectionsToScore(newData);
    setDeletedData(
      (prevData) =>
        new ImagingSchedulesDeletedDataDTO({
          ...prevData,
          imagingTaskClearedFromToScoreIds: Array.from(
            new Set([...(prevData?.imagingTaskClearedFromToScoreIds || []), ...ids]),
          ),
        }),
    );
    dispatch(setImagingSchedulesIsDirty(true));
    setSelectedIds([]);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setSelectedOption(selectedValue);
    setSelectedIds([]);
  };

  const onCheckboxSelectionChange = (rowIndices: number[]) => {
    setSelectedIds([...rowIndices]);
  };

  const handleOnRowCLick = (row) => {
    setSelectedIds([row.index]);
  };

  const handleRowDoubleClick = (row: any) => {
    window.open(`/exp/${row.original.experimentId}`, `/exp/${row.original.experimentId}`);
  };

  const deleteButton = getHeaderDeleteButton(onDelete, selectedIds?.length > 0);

  return (
    <div id="inspectiontoscore-wrapper" className="flex flex-col h-[90%]">
      <div
        id="inspectiontoscore-container"
        className="flex flex-row items-center justify-between py-2"
      >
        <div id="inspectiontoscore-title" className="text-lg font-bold mx-2">
          Plate Inspections To Score
        </div>
        <div
          id="inspectiontoscore-lists"
          className="flex flex-row text-lg font-bold mx-2 gap-2"
        >
          <RMCustomButton {...deleteButton} />
          <select
            id="inspectiontoscore-option"
            value={selectedOption}
            onChange={handleSelectChange}
          >
            {DateFilters.map((f, i) => (
              <option
                id={`inspectiontoscore-option-datefilter${i}`}
                key={`dateFilter${i}`}
                value={f.value}
              >
                {f.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <RMTable
        columns={PlateInspectionsToScoreCols}
        data={tableData}
        tableClass="w-full"
        selectedIds={selectedIds}
        onCheckboxSelectionChange={onCheckboxSelectionChange}
        onRowClicked={handleOnRowCLick}
        onRowDoubleClicked={handleRowDoubleClick}
        enableMultiSelection
      />
    </div>
  );
}

export default InspectionsToScore;
